import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import ApprovalWorkflowService from "../services/approvalWorkflow.service";
import { IApprovalSystemState } from "../types";
import { StoreState } from "@/store/type";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { formatString } from "@/core/utils/helpers";
import analyticsService from "@/analytic.service";
import { APPROVAL_WORKFLOW_ANALYTICS_EVENTS } from "../constants/analyticsEvents";
import { CreateOrUpdateApprovalWorkflowDto } from "../dtos/approvalWorkflow.dtos";
import { unwrapApprovalWorkflowPayload } from "../utils/normalizeApprovalWorkflow";

const approvalWorkflowService = new ApprovalWorkflowService();

const actions: ActionTree<IApprovalSystemState, StoreState> = {
  async fetchApprovalWorkflows(
    context: ActionContext<IApprovalSystemState, StoreState>,
    payload: { organizationId: string; isActive?: boolean },
  ) {
    try {
      context.commit("setFetchingApprovalWorkflows", true);
      const { organizationId, isActive } = payload;
      const response: ICoreResponse =
        await approvalWorkflowService.getApprovalWorkflows(
          organizationId,
          isActive,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const workflows = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        context.commit("setApprovalWorkflows", workflows);
        analyticsService.logActionEvent(
          APPROVAL_WORKFLOW_ANALYTICS_EVENTS.APPROVAL_WORKFLOW_FETCH,
          { organizationId, isActive },
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setApprovalWorkflows", []);
    } finally {
      context.commit("setFetchingApprovalWorkflows", false);
    }
  },

  async fetchApprovalWorkflowDetail(
    context: ActionContext<IApprovalSystemState, StoreState>,
    payload: {
      organizationId: string;
      workflowId: string;
      successHandler?: () => void;
      errorHandler?: () => void;
    },
  ) {
    try {
      context.commit("setFetchingApprovalWorkflowDetail", true);
      context.commit("setSelectedApprovalWorkflow", null);
      const { organizationId, workflowId, successHandler, errorHandler } =
        payload;
      const response: ICoreResponse =
        await approvalWorkflowService.getApprovalWorkflowDetail(
          organizationId,
          workflowId,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const detail =
          unwrapApprovalWorkflowPayload(response.data) || response.data;
        context.commit("setSelectedApprovalWorkflow", detail);
        analyticsService.logActionEvent(
          APPROVAL_WORKFLOW_ANALYTICS_EVENTS.APPROVAL_WORKFLOW_DETAIL_FETCH,
          { organizationId, workflowId },
        );
        successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setSelectedApprovalWorkflow", null);
      payload.errorHandler?.();
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchingApprovalWorkflowDetail", false);
    }
  },

  async createOrUpdateApprovalWorkflow(
    context: ActionContext<IApprovalSystemState, StoreState>,
    {
      organizationId,
      body,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      body: CreateOrUpdateApprovalWorkflowDto;
      successHandler: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    const isUpdate = Boolean(body.public_id);
    try {
      context.commit("setApprovalWorkflowActionLoading", true);
      const response: ICoreResponse =
        await approvalWorkflowService.createOrUpdateApprovalWorkflow(
          organizationId,
          body,
        );
      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        successHandler();
        analyticsService.logActionEvent(
          isUpdate
            ? APPROVAL_WORKFLOW_ANALYTICS_EVENTS.APPROVAL_WORKFLOW_UPDATE
            : APPROVAL_WORKFLOW_ANALYTICS_EVENTS.APPROVAL_WORKFLOW_CREATE,
          { organizationId, name: body.name, publicId: body.public_id },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          (isUpdate
            ? NOTIFICATION_MESSAGES.APPROVAL_WORKFLOW_UPDATE_SUCCESS
            : NOTIFICATION_MESSAGES.APPROVAL_WORKFLOW_CREATE_SUCCESS);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      if (errorHandler) {
        errorHandler(exception);
      }
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalWorkflowActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async deleteApprovalWorkflow(
    context: ActionContext<IApprovalSystemState, StoreState>,
    {
      organizationId,
      publicId,
      successHandler,
    }: {
      organizationId: string;
      publicId: string;
      successHandler: () => void;
    },
  ) {
    try {
      context.commit("setApprovalWorkflowActionLoading", true);
      const response: ICoreResponse =
        await approvalWorkflowService.deleteApprovalWorkflow(
          organizationId,
          publicId,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedWorkflows = context.state.approvalWorkflows.filter(
          (workflow) => workflow.public_id !== publicId,
        );
        context.commit("setApprovalWorkflows", updatedWorkflows);
        successHandler();
        analyticsService.logActionEvent(
          APPROVAL_WORKFLOW_ANALYTICS_EVENTS.APPROVAL_WORKFLOW_DELETE,
          { organizationId, publicId },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.APPROVAL_WORKFLOW_DELETE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalWorkflowActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async patchApprovalWorkflowStatus(
    context: ActionContext<IApprovalSystemState, StoreState>,
    {
      organizationId,
      workflowId,
      workflowName,
      isActive,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      workflowId: string;
      workflowName: string;
      isActive: boolean;
      successHandler?: () => void;
      errorHandler?: () => void;
    },
  ) {
    try {
      context.commit("setApprovalWorkflowActionLoading", true);
      const response: ICoreResponse =
        await approvalWorkflowService.patchApprovalWorkflowStatus(
          organizationId,
          workflowId,
          isActive,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedWorkflows = context.state.approvalWorkflows.map(
          (workflow) => {
            if (workflow.public_id === workflowId) {
              return { ...workflow, is_active: isActive };
            }
            return workflow;
          },
        );
        context.commit("setApprovalWorkflows", updatedWorkflows);
        successHandler?.();
        analyticsService.logActionEvent(
          APPROVAL_WORKFLOW_ANALYTICS_EVENTS.APPROVAL_WORKFLOW_STATUS_TOGGLE,
          { organizationId, workflowId, workflowName, isActive },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = formatString(
          NOTIFICATION_MESSAGES.APPROVAL_WORKFLOW_STATUS_UPDATE_SUCCESS,
          workflowName,
          isActive ? "active" : "inactive",
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      errorHandler?.();
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalWorkflowActionLoading", false);
      notificationService.triggerNotification();
    }
  },
};

export default actions;
